import React, { useEffect, useRef, useCallback, useState, useMemo } from 'react';
import { FaCrown, FaBox, FaRecycle, FaMapMarkerAlt, FaTag, FaHeart, FaEye, FaSync, FaExclamationTriangle } from 'react-icons/fa';
import { getMediaUrl } from '../utils/urlHelpers';
import { 
  isProductPromoted, 
  getPromotionTypeLabel, 
  getPromotionBadgeClass 
} from '../utils/promotionUtils';
import '../styles/product-cards-v2.css';
import error from '../assets/Tr3.jpg';
import { InfinityLoadingSkeleton } from './Skeletons';
import CampaignCard from './CampaignCard';

const ProductsGridV2 = ({ 
    products, 
    likedProducts = [], 
    loggedInUser, 
    handleProductClick, 
    handleLikeClick, 
    hasMore, 
    fetchMoreData, 
    loading,
    loadingMore = false,
    error: gridError,
    onRefresh,
    campaigns = [],
    onCampaignPress
}) => {
    const loadingRef = useRef(null);
    const [refreshing, setRefreshing] = useState(false);
    const [hasError, setHasError] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [animatingLikes, setAnimatingLikes] = useState(new Set()); // Track which products are animating
    const observerRef = useRef(null);
    
    // Handle grid errors
    useEffect(() => {
        if (gridError) {
            setHasError(true);
            setErrorMessage(typeof gridError === 'string' ? gridError : 'An error occurred while loading products.');
        } else {
            setHasError(false);
            setErrorMessage('');
        }
    }, [gridError]);

    // Intersection Observer for infinity scroll with error handling
    const handleIntersection = useCallback((entries) => {
        try {
            const target = entries[0];
            if (target.isIntersecting && hasMore && !loading && !hasError) {
                fetchMoreData();
            }
        } catch (error) {

        }
    }, [hasMore, loading, fetchMoreData, hasError]);

    useEffect(() => {
        try {
            observerRef.current = new IntersectionObserver(handleIntersection, {
                threshold: 0.1, // Trigger when 10% of the element is visible
                rootMargin: '150px' // Increased margin for earlier loading
            });

            const currentLoadingRef = loadingRef.current;
            if (currentLoadingRef) {
                observerRef.current.observe(currentLoadingRef);
            }

            return () => {
                if (observerRef.current && currentLoadingRef) {
                    observerRef.current.unobserve(currentLoadingRef);
                    observerRef.current.disconnect();
                }
            };
        } catch (error) {
            console.error('Failed to set up IntersectionObserver:', error);
        }
    }, [handleIntersection]);
    
    // Handle pull-to-refresh (if supported by the container)
    const handleRefresh = useCallback(async () => {
        if (!onRefresh || refreshing) return;
        
        try {
            setRefreshing(true);
            await onRefresh();
        } catch (err) {
            console.error('Refresh error:', err);
            setHasError(true);
            setErrorMessage('Failed to refresh. Please try again.');
        } finally {
            setRefreshing(false);
        }
    }, [onRefresh, refreshing]);
    
    // Simplified image error handling (optimized for performance)
    const handleImageError = (e) => {
        console.error('Image failed to load:', e.target.src);
        e.target.src = error;
    };

    // Clean and normalize product descriptions to prevent layout inconsistencies
    const cleanDescription = (description) => {
        if (!description) return '';
        
        try {
            // Remove excessive HTML tags while preserving basic formatting
            let cleaned = description
                // Remove dangerous HTML tags
                .replace(/<script[^>]*>.*?<\/script>/gi, '')
                .replace(/<style[^>]*>.*?<\/style>/gi, '')
                .replace(/<iframe[^>]*>.*?<\/iframe>/gi, '')
                
                // Normalize line breaks and spacing
                .replace(/(<br\s*\/?>){3,}/gi, '<br><br>') // Max 2 consecutive line breaks
                .replace(/(&nbsp;|\s){4,}/g, '   ') // Max 3 consecutive spaces
                .replace(/\n{3,}/g, '\n\n') // Max 2 consecutive newlines
                
                // Remove excessive paragraph spacing
                .replace(/(<p[^>]*>\s*<\/p>){2,}/gi, '<p></p>') // Max 1 empty paragraph
                .replace(/(<div[^>]*>\s*<\/div>){2,}/gi, '<div></div>') // Max 1 empty div
                
                // Clean up common formatting issues
                .replace(/\s+/g, ' ') // Multiple spaces to single space
                .replace(/(<[^>]+>)\s+/g, '$1') // Remove space after opening tags
                .replace(/\s+(<[^>]+>)/g, '$1') // Remove space before closing tags
                
                // Limit text length to prevent extremely long descriptions
                .substring(0, 500) // Max 500 characters
                .trim();
            
            // If description is still too long after HTML, truncate at word boundary
            if (cleaned.length > 300) {
                const textContent = cleaned.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
                if (textContent.length > 150) {
                    const truncated = textContent.substring(0, 147);
                    const lastSpace = truncated.lastIndexOf(' ');
                    const finalText = lastSpace > 100 ? truncated.substring(0, lastSpace) : truncated;
                    return finalText + '...';
                }
            }
            
            return cleaned;
        } catch (error) {

            // Fallback: return plain text only
            return description.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim().substring(0, 150) + '...';
        }
    };

const formatPrice = (price) => {
    try {
        const numPrice = typeof price === 'string' ? parseFloat(price) : price;
        
        if (typeof numPrice !== 'number' || isNaN(numPrice) || numPrice < 0) {
            return '0';
        }
        
        if (numPrice >= 1000) {
            return `${(numPrice / 1000).toLocaleString('fr-FR', {
                maximumFractionDigits: 1,
                minimumFractionDigits: 0
            })}k`;
        }
        return numPrice.toLocaleString('fr-FR', {
            maximumFractionDigits: 0
        });
    } catch (error) {

        return '0'; // Safe fallback
    }
};

    const handleLikeClickLocal = useCallback(async (e, productId) => {
        try {
            e.preventDefault();
            e.stopPropagation();

            // Add to animating set
            setAnimatingLikes(prev => new Set(prev).add(productId));

            const isLikedByUser = likedProducts.includes(productId);

            if (typeof handleLikeClick === 'function') {
                await handleLikeClick(productId, isLikedByUser);
            }

            // Remove from animating set after animation duration
            setTimeout(() => {
                setAnimatingLikes(prev => {
                    const newSet = new Set(prev);
                    newSet.delete(productId);
                    return newSet;
                });
            }, 600);
        } catch (error) {
            console.error('Error handling like action:', error);
            // Remove from animating set on error
            setAnimatingLikes(prev => {
                const newSet = new Set(prev);
                newSet.delete(productId);
                return newSet;
            });
        }
    }, [likedProducts, handleLikeClick]);
    
    // Retry loading on error
    const handleRetry = useCallback(() => {
        if (onRefresh) {
            handleRefresh();
        } else if (fetchMoreData) {
            setHasError(false);
            fetchMoreData();
        }
    }, [onRefresh, fetchMoreData, handleRefresh]);

    // Shuffle campaigns for random order (Fisher-Yates shuffle algorithm)
    // MUST be called before any conditional returns (React Hooks rules)
    const shuffledCampaigns = useMemo(() => {
        if (!campaigns || campaigns.length === 0) return [];
        const shuffled = [...campaigns];
        for (let i = shuffled.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
        }
        return shuffled;
    }, [campaigns]);

    // Inject campaigns into product feed (every 7 products)
    // MUST be called before any conditional returns (React Hooks rules)
    const INJECT_EVERY = 7;
    const mixedItems = useMemo(() => {
        if (!products || products.length === 0) return [];
        
        const result = [];
        let campaignIndex = 0;
        
        products.forEach((product, index) => {
            result.push({ type: 'product', data: product });
            
            // Inject campaign every INJECT_EVERY products
            if (shuffledCampaigns.length > 0 && (index + 1) % INJECT_EVERY === 0) {
                const campaign = shuffledCampaigns[campaignIndex % shuffledCampaigns.length];
                result.push({ type: 'campaign', data: campaign });
                campaignIndex++;
            }
        });
        
        return result;
    }, [products, shuffledCampaigns]);

    // Error state handling
    if (hasError) {
        return (
            <div className="products-grid-empty">
                <FaExclamationTriangle />
                <p>{errorMessage || "Une erreur est survenue lors du chargement des produits."}</p>
                <button onClick={handleRetry} className="retry-button">
                    <FaSync /> Réessayer
                </button>
            </div>
        );
    }

    // Empty state handling with better UX
    if (!products || products.length === 0) {
        return loading ? (
            <div className="products-grid-v2">
                <ProductGridSkeleton count={6} />
            </div>
        ) : (
            <div className="products-grid-empty">
                <p>Aucun produit disponible pour le moment.</p>
                {onRefresh && (
                    <button onClick={handleRefresh} className="retry-button">
                        <FaSync /> Rafraîchir
                    </button>
                )}
            </div>
        );
    }

    return (
        <>
            {/* Pull-to-refresh indicator (if applicable) */}
            {refreshing && (
                <div className="refresh-indicator">
                    <FaSync /> Rafraîchissement...
                </div>
            )}
            
            <div className="products-grid-v2">
                {mixedItems.map((item, index) => {
                    if (item.type === 'campaign') {
                        return (
                            <div key={`campaign-${item.data.id || index}`} className="campaign-grid-item">
                                <CampaignCard 
                                    campaign={item.data} 
                                    onClick={onCampaignPress}
                                />
                            </div>
                        );
                    }

                    const product = item.data;
                    const isLikedByUser = likedProducts.includes(product.id);
                    
                    // Use centralized promotion detection utility
                    const isPromoted = isProductPromoted(product);
                    const promotionLabel = getPromotionTypeLabel(product);
                    const promotionBadgeClass = getPromotionBadgeClass(product);
                    
                    const formattedPrice = formatPrice(product.price);
                    const priceDisplay = product.negotiable
                        ? `À partir de ${product.currency} ${formattedPrice}`
                        : `${product.currency} ${formattedPrice}`;
                    
                    // Build image URL with fallbacks - handle both dashboard and product details data structures
                    const imageUrl = product.first_image
                        ? getMediaUrl(product.first_image)
                        : product.images && product.images.length > 0
                            ? getMediaUrl(product.images[0].url || product.images[0].image)
                            : product.media && product.media.length > 0
                                ? getMediaUrl(product.media[0].url || product.media[0].image)
                                : error;

                    return (
                        <div 
                            key={product.id}
                            className="product-card-v2"
                            onClick={(e) => handleProductClick(product.uuid, e)}  // Use product.uuid, pass event
                        >
                            {/* Image Section */}
                            <div className="product-image-section-v2">
                                <img
                                    src={imageUrl}
                                    alt={product.name}
                                    className="product-image-v2"
                                    loading="lazy"
                                    onError={handleImageError}
                                />
                                
                                {/* Price Overlay */}
                                <div className="product-price-overlay-v2">
                                    {priceDisplay}
                                </div>

                                {/* Condition Badge */}
                                <div className={`product-condition-badge-v2 ${product.condition === 'Usagé' ? 'used' : 'new'}`}>
                                    {product.condition === 'Usagé' ? (
                                        <FaRecycle />
                                    ) : (
                                        <FaBox />
                                    )}
                                </div>

                                {/* Promoted Badge */}
                                {isPromoted && (
                                    <div className={`product-promoted-badge-v2 ${promotionBadgeClass}`}>
                                        <FaCrown />
                                        <span>{promotionLabel}</span>
                                    </div>
                                )}
                            </div>

                            {/* Content Section */}
                            <div className="product-content-v2">
                                {/* Product Name */}
                                <h3 className="product-name-v2">
                                    {product.name || 'Produit sans nom'}
                                </h3>

                                {/* Product Description */}
                                <div 
                                    className="product-description-v2"
                                    dangerouslySetInnerHTML={{ __html: cleanDescription(product.description) }}
                                />

                                {/* Meta Section */}
                                <div className="product-meta-v2">
                                    {/* Location and Category */}
                                    <div className="product-location-category-v2">
                                        <div className="product-location-v2">
                                            <FaMapMarkerAlt className="icon" />
                                            <span>{product.city_name || 'Lieu inconnu'}</span>
                                        </div>
                                        <div className="product-category-v2">
                                            <FaTag className="icon" />
                                            <span>{product.category_name || 'Catégorie inconnue'}</span>
                                        </div>
                                    </div>

                                    {/* Stats */}
                                    <div className="product-stats-v2">
                                        <div
                                            className={`product-likes-v2 ${isLikedByUser ? 'liked' : ''} ${animatingLikes.has(product.uuid || product.id) ? 'animating' : ''}`}
                                            onClick={(e) => handleLikeClickLocal(e, product.uuid || product.id)}
                                        >
                                            <FaHeart className="icon" />
                                            {product.likes > 0 ? <span>{product.likes}</span> : null}
                                        </div>
                                        <div className="product-views-v2">
                                            <FaEye className="icon" />
                                            {product.views > 0 ? <span>{product.views}</span> : null}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })}

                {/* Infinity Loading Skeleton that matches the grid design */}
                {hasMore && (
                    <div ref={loadingRef} className="infinity-loading-container">
                        {loadingMore && <InfinityLoadingSkeleton />}
                    </div>
                )}
                
                {/* Loading indicator for initial load */}
                {products.length === 0 && loading && (
                    <div className="loading-text">
                        <FaSync /> Chargement des produits...
                    </div>
                )}
            </div>
        </>
    );
};

// Skeleton component for product grid with variable count
const ProductGridSkeleton = ({ count = 8 }) => {
    return (
        <div className="products-grid-v2-skeleton">
            {Array.from({ length: count }).map((_, i) => (
                <div key={i} className="product-skeleton-item-v2">
                    <div className="product-skeleton-image-section">
                        <div className="skeleton-image-v2 skeleton-shimmer"></div>
                        <div className="skeleton-price-overlay skeleton-shimmer">
                            <div className="skeleton-price-text skeleton-shimmer"></div>
                        </div>
                        <div className="skeleton-condition-badge skeleton-shimmer"></div>
                        {i % 3 === 0 && (
                            <div className="skeleton-promoted-badge skeleton-shimmer">
                                <div className="skeleton-crown-icon skeleton-shimmer"></div>
                                <div className="skeleton-promoted-text skeleton-shimmer"></div>
                            </div>
                        )}
                    </div>
                    <div className="product-skeleton-content-v2">
                        <div className="skeleton-title-v2 skeleton-shimmer"></div>
                        <div className="skeleton-description-v2">
                            <div className="skeleton-description-line-1 skeleton-shimmer"></div>
                            <div className="skeleton-description-line-2 skeleton-shimmer"></div>
                            <div className="skeleton-description-line-3 skeleton-shimmer"></div>
                        </div>
                        <div className="product-skeleton-meta-v2">
                            <div className="skeleton-location-category">
                                <div className="skeleton-location-v2">
                                    <div className="skeleton-location-icon skeleton-shimmer"></div>
                                    <div className="skeleton-location-text skeleton-shimmer"></div>
                                </div>
                                <div className="skeleton-category-v2">
                                    <div className="skeleton-category-icon skeleton-shimmer"></div>
                                    <div className="skeleton-category-text skeleton-shimmer"></div>
                                </div>
                            </div>
                            <div className="skeleton-stats-v2">
                                <div className="skeleton-likes-v2">
                                    <div className="skeleton-heart-icon skeleton-shimmer"></div>
                                    <div className="skeleton-likes-count skeleton-shimmer"></div>
                                </div>
                                <div className="skeleton-views-v2">
                                    <div className="skeleton-eye-icon skeleton-shimmer"></div>
                                    <div className="skeleton-views-count skeleton-shimmer"></div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
};

export default React.memo(ProductsGridV2);